import { ContractStatusEnum } from 'gql/graphql';
import type { ContractOtherCostUpdateData } from 'gql/graphql';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';

import { useMemo } from 'react';

import { TabsIds } from '../../OtherCreate/commonData';

interface IUseUerUpdateContractOtherCostCanEdit {
    data?: ContractOtherCostUpdateData;
    contractStatus?: ContractStatusEnum | null;
}

export enum UpdateOtherCostEditable {
    name = 'name',
    key = 'key',
    calculatingType = 'calculatingType',
    calculatingTypeValue = 'calculatingTypeValue',
    type = 'type',
    constructionType = 'constructionType',
    constructionTypeExclude = 'constructionTypeExclude',
    cost = 'cost',
    distributionMethod = 'distributionMethod',
    limit = 'limit',
    limitSpan = 'limitSpan',
    canEdit = 'canEdit',
    limits = 'limits',
    section = 'section',
    canUseOtherSpans = 'canUseOtherSpans',
}

export interface IUpdateOtherCostCanEditCommon {
    [UpdateOtherCostEditable.name]: boolean;
    [UpdateOtherCostEditable.key]: boolean;
    [UpdateOtherCostEditable.calculatingType]: boolean;
    [UpdateOtherCostEditable.calculatingTypeValue]: boolean;
    [UpdateOtherCostEditable.type]: boolean;
    [UpdateOtherCostEditable.canEdit]: boolean;
    [UpdateOtherCostEditable.section]: boolean;
}

export type TUpdateOtherCostCanEditPriceLimits = Record<
    TConstructionTypesIndexes,
    {
        [UpdateOtherCostEditable.constructionType]: boolean;
        [UpdateOtherCostEditable.constructionTypeExclude]: boolean;
        [UpdateOtherCostEditable.cost]: boolean;
        [UpdateOtherCostEditable.distributionMethod]: boolean;
        [UpdateOtherCostEditable.limit]: boolean;
        [UpdateOtherCostEditable.limitSpan]: boolean;
    }
>;

export interface IUpdateOtherCostCanEditPrice {
    [UpdateOtherCostEditable.canEdit]: boolean;
    [UpdateOtherCostEditable.limits]: TUpdateOtherCostCanEditPriceLimits | undefined;
    [UpdateOtherCostEditable.canUseOtherSpans]: boolean;
}

export interface IUpdateContractOtherCostCanEdit {
    [TabsIds.COMMON]: IUpdateOtherCostCanEditCommon;
    [TabsIds.PRICE]: IUpdateOtherCostCanEditPrice | undefined;
    [TabsIds.FORMULA]: boolean;
}

export const useUerUpdateOtherCostCanEdit = (
    props: IUseUerUpdateContractOtherCostCanEdit,
): IUpdateContractOtherCostCanEdit => {
    const { data, contractStatus } = props;

    const contractNotCompletedOrDelayed = contractStatus
        ? ![ContractStatusEnum.Completed, ContractStatusEnum.Delayed].includes(contractStatus)
        : true;

    return useMemo(
        () => ({
            [TabsIds.COMMON]: {
                [UpdateOtherCostEditable.name]: !!data?.canEditName,
                [UpdateOtherCostEditable.key]: contractNotCompletedOrDelayed,
                [UpdateOtherCostEditable.calculatingType]: !!data?.canEditCalculationType,
                [UpdateOtherCostEditable.calculatingTypeValue]: !!data?.canEditCalculationTypeValue,
                [UpdateOtherCostEditable.type]: !!data?.canEditType,
                [UpdateOtherCostEditable.section]: !!data?.canEditSection,
                [UpdateOtherCostEditable.canEdit]: !!data?.canEdit,
            },
            [TabsIds.PRICE]: {
                [UpdateOtherCostEditable.canEdit]: !!data?.canEditLimits,
                [UpdateOtherCostEditable.limits]: data?.groupConstructionLimits?.reduce((acc, limit) => {
                    acc[limit.constructionType.id as unknown as TConstructionTypesIndexes] = {
                        [UpdateOtherCostEditable.constructionType]: !!limit.canEditConstructionType,
                        [UpdateOtherCostEditable.constructionTypeExclude]: !!limit.canExclude,
                        [UpdateOtherCostEditable.cost]: !!limit.canEditCost,
                        [UpdateOtherCostEditable.distributionMethod]: !!limit.canEditDistributionMethod,
                        [UpdateOtherCostEditable.limit]: !!limit.canEditLimit,
                        [UpdateOtherCostEditable.limitSpan]: !!limit.canEditLimitSpan,
                    };

                    return acc;
                }, {} as TUpdateOtherCostCanEditPriceLimits),
                [UpdateOtherCostEditable.canUseOtherSpans]: !!data?.canUseOtherSpans,
            },
            [TabsIds.FORMULA]: !!data?.isFormulaEditable,
        }),
        [
            contractNotCompletedOrDelayed,
            data?.canEdit,
            data?.canEditCalculationType,
            data?.canEditCalculationTypeValue,
            data?.canEditLimits,
            data?.canEditName,
            data?.canEditSection,
            data?.canEditType,
            data?.canUseOtherSpans,
            data?.groupConstructionLimits,
            data?.isFormulaEditable,
        ],
    );
};
